"use client"

/**
 * Shell-level question inspector — same rail stack as library secondary nav
 * (`ResizablePanel` + `ResizableHandle` + `NestedSecondaryPanelShell`).
 *
 * Body fields portal from {@link QuestionComposerProvider} into the shell host
 * so form context survives the DOM move.
 */

import * as React from "react"
import { createPortal } from "react-dom"

import { Form } from "@/components/ui/form"
import { NestedSecondaryPanelShell } from "@/components/templates/nested-secondary-panel-shell"
import {
  ResizableHandle,
  ResizablePanel,
} from "@/components/ui/resizable"
import { NAV_FLYOUT_SCROLL_BODY } from "@exxatdesignux/ui/lib/nav-flyout-inset"
import { cn } from "@/lib/utils"
import { useShellRailWidth } from "@exxatdesignux/ui/hooks/use-shell-rail-width"
import { useSidebar } from "@/components/ui/sidebar"
import { useIsMobile } from "@/hooks/use-mobile"
import { useSidebarReflowZoom } from "@/hooks/use-sidebar-reflow-zoom"
import { SecondaryPanelFlyoutHeader } from "@/components/sidebar"
import { SecondaryHubNavCompactShell } from "@/components/sidebar"

import {
  useQuestionComposerContext,
  useQuestionComposerInspectorSurface,
} from "./composer-context"
import {
  QuestionComposerInspectorFields,
  QuestionInspectorCompactNav,
} from "./composer-inspector"

export const QUESTION_INSPECTOR_WIDTH_KEY = "shell:question-inspector-width"

/**
 * The inspector's own widths, not the secondary nav's.
 *
 * It inherited `SECONDARY_PANEL_WIDTH_*` — 256 wide, 200 minimum —
 * which are sized for a column of links. This panel holds the Leo
 * spotted card, the properties and the answer key, and at 256 the card
 * header ran out of room and clipped its pager. The floor is what
 * matters most: below about 320 the header cannot fit a label, a badge
 * and two chevrons on one row whatever it does.
 */
export const QUESTION_INSPECTOR_WIDTH_DEFAULT = 360
export const QUESTION_INSPECTOR_WIDTH_MIN = 320
export const QUESTION_INSPECTOR_WIDTH_MAX = 560

function clampQuestionInspectorWidth(width: number): number {
  return Math.min(
    QUESTION_INSPECTOR_WIDTH_MAX,
    Math.max(QUESTION_INSPECTOR_WIDTH_MIN, Math.round(width)),
  )
}

type QuestionInspectorShellBridgeState = {
  inspectorOpen: boolean
  setInspectorOpen: React.Dispatch<React.SetStateAction<boolean>>
}

type QuestionInspectorShellSetters = {
  setBridge: (value: QuestionInspectorShellBridgeState | null) => void
  setBodyHost: (element: HTMLElement | null) => void
}

/**
 * Value and setters are separate contexts on purpose.
 *
 * One context would put the registered bridge in the dependency list of the
 * effect that registers it, so every claim would schedule the next one and the
 * composer would re-register until React gave up mid-navigation.
 */
const QuestionInspectorShellValueContext = React.createContext<{
  bridge: QuestionInspectorShellBridgeState | null
  bodyHost: HTMLElement | null
} | null>(null)

const QuestionInspectorShellSettersContext =
  React.createContext<QuestionInspectorShellSetters | null>(null)

function sameBridge(
  a: QuestionInspectorShellBridgeState | null,
  b: QuestionInspectorShellBridgeState | null,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.inspectorOpen === b.inspectorOpen &&
    a.setInspectorOpen === b.setInspectorOpen
  )
}

export function QuestionInspectorShellBridgeProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [bridge, setBridgeState] =
    React.useState<QuestionInspectorShellBridgeState | null>(null)
  const [bodyHost, setBodyHostState] = React.useState<HTMLElement | null>(null)
  const setters = React.useMemo<QuestionInspectorShellSetters>(
    () => ({
      setBridge: (next) =>
        setBridgeState((prev) => (sameBridge(prev, next) ? prev : next)),
      setBodyHost: (element) =>
        setBodyHostState((prev) => (prev === element ? prev : element)),
    }),
    [],
  )
  const value = React.useMemo(() => ({ bridge, bodyHost }), [bridge, bodyHost])
  return (
    <QuestionInspectorShellSettersContext.Provider value={setters}>
      <QuestionInspectorShellValueContext.Provider value={value}>
        {children}
      </QuestionInspectorShellValueContext.Provider>
    </QuestionInspectorShellSettersContext.Provider>
  )
}

function useQuestionInspectorShellBridge() {
  const value = React.useContext(QuestionInspectorShellValueContext)
  const setters = React.useContext(QuestionInspectorShellSettersContext)
  if (!value || !setters) {
    throw new Error(
      "Question inspector shell requires QuestionInspectorShellBridgeProvider in the app layout.",
    )
  }
  return { ...value, ...setters }
}

/** True when the app shell can host the compose Properties rail. */
export function useQuestionInspectorShellProvider() {
  return React.useContext(QuestionInspectorShellSettersContext) != null
}

function QuestionInspectorPanelBody({
  inspectorOpen,
  setInspectorOpen,
}: {
  inspectorOpen: boolean
  setInspectorOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { categoryPickerOpen, historyPickerOpen } = useQuestionComposerContext()
  const inspectorNavOpen = categoryPickerOpen || historyPickerOpen
  const isMobile = useIsMobile()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const showCompactRail = !navFlyout && !inspectorOpen

  if (showCompactRail) {
    return (
      <SecondaryHubNavCompactShell
        ariaLabel="Question properties"
        side="right"
        onExpand={() => setInspectorOpen(true)}
      >
        <QuestionInspectorCompactNav />
      </SecondaryHubNavCompactShell>
    )
  }

  return (
    <>
      <SecondaryPanelFlyoutHeader
        flyout={navFlyout}
        side="right"
        onCollapse={() => setInspectorOpen(false)}
        onCloseFlyout={() => setInspectorOpen(false)}
      />
      <div
        className={cn(
          NAV_FLYOUT_SCROLL_BODY,
          "[overflow-anchor:none]",
          inspectorNavOpen && "flex min-h-0 flex-1 flex-col overflow-hidden",
        )}
        data-composer-inspector-scroll=""
      >
        <div
          className={cn(
            "flex flex-col gap-4 px-3 py-3",
            inspectorNavOpen && "min-h-0 flex-1 flex-col gap-2 py-2",
          )}
        >
          <QuestionComposerInspectorFields />
        </div>
      </div>
    </>
  )
}

/**
 * Registers open state and portals inspector fields into the shell body host.
 * Render inside {@link QuestionComposerProvider}.
 */
export function QuestionComposerInspectorShellBridge() {
  const { form, inspectorOpen, setInspectorOpen } = useQuestionComposerContext()
  const inspectorSurface = useQuestionComposerInspectorSurface()
  const setters = React.useContext(QuestionInspectorShellSettersContext)
  const value = React.useContext(QuestionInspectorShellValueContext)
  const bodyHost = value?.bodyHost ?? null

  React.useEffect(() => {
    if (!setters || inspectorSurface !== "secondary-panel") {
      setters?.setBridge(null)
      return
    }
    setters.setBridge({ inspectorOpen, setInspectorOpen })
    return () => setters.setBridge(null)
  }, [setters, inspectorOpen, inspectorSurface, setInspectorOpen])

  if (!setters || inspectorSurface !== "secondary-panel" || !bodyHost) {
    return null
  }

  return createPortal(
    <Form {...form}>
      <QuestionInspectorPanelBody
        inspectorOpen={inspectorOpen}
        setInspectorOpen={setInspectorOpen}
      />
    </Form>,
    bodyHost,
  )
}

/**
 * Docked inspector rail — `ResizableHandle` + `ResizablePanel` in the workspace
 * group. Mount once in `App.tsx` beside {@link AskLeoDockedSlot}.
 */
export function QuestionComposerInspectorDockedSlot() {
  const { bridge, setBodyHost } = useQuestionInspectorShellBridge()
  const { isMobile } = useSidebar()
  const reflowZoom = useSidebarReflowZoom()
  const navFlyout = isMobile || reflowZoom
  const rail = useShellRailWidth({
    storageKey: QUESTION_INSPECTOR_WIDTH_KEY,
    defaultWidth: QUESTION_INSPECTOR_WIDTH_DEFAULT,
    minWidth: QUESTION_INSPECTOR_WIDTH_MIN,
    maxWidth: QUESTION_INSPECTOR_WIDTH_MAX,
    clamp: clampQuestionInspectorWidth,
    // Bumped with the widths: a rail persisted at the old 256 would
    // otherwise keep the panel below its new floor.
    version: 4,
  })

  React.useEffect(() => {
    return () => setBodyHost(null)
  }, [setBodyHost])

  if (!bridge) {
    return null
  }

  const { inspectorOpen, setInspectorOpen } = bridge
  const overlay: false | "mobile" | "desktop" = navFlyout
    ? isMobile
      ? "mobile"
      : "desktop"
    : false
  const shellOpen = navFlyout ? inspectorOpen : true
  const compact = !navFlyout && !inspectorOpen
  const dockedResizable = shellOpen && !compact && overlay === false

  const bodyHost = (
    <div
      ref={setBodyHost}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-question-inspector-body-host=""
    />
  )

  const shell = (
    <NestedSecondaryPanelShell
      open={shellOpen}
      compact={compact}
      overlay={overlay}
      side="right"
      dataSlot="question-inspector-panel"
      widthPersistKey={QUESTION_INSPECTOR_WIDTH_KEY}
      widthDefault={QUESTION_INSPECTOR_WIDTH_DEFAULT}
      ownSize={!dockedResizable && !(shellOpen && compact)}
      aria-label="Question inspector"
      className="h-full min-h-0 self-stretch"
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      {bodyHost}
    </NestedSecondaryPanelShell>
  )

  if (overlay !== false) {
    if (!shellOpen) return null
    return (
      <>
        <button
          type="button"
          aria-label="Close question inspector"
          className="fixed inset-0 z-[60] cursor-default bg-background/60"
          onClick={() => setInspectorOpen(false)}
        />
        {shell}
      </>
    )
  }

  if (!shellOpen) return null

  if (compact) {
    return (
      <>
        <ResizableHandle withHandle />
        <ResizablePanel
          id="shell-question-inspector-compact"
          defaultSize="3rem"
          minSize="3rem"
          maxSize="3rem"
          groupResizeBehavior="preserve-pixel-size"
          collapsible={false}
          className="min-h-0 min-w-0"
        >
          <NestedSecondaryPanelShell
            open
            compact
            overlay={false}
            side="right"
            ownSize={false}
            aria-label="Question inspector"
            contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            {bodyHost}
          </NestedSecondaryPanelShell>
        </ResizablePanel>
      </>
    )
  }

  return (
    <>
      <ResizableHandle withHandle />
      <ResizablePanel
        id="shell-question-inspector"
        {...rail.panelProps}
        className="min-h-0 min-w-0"
      >
        {shell}
      </ResizablePanel>
    </>
  )
}

/** @deprecated Use {@link QuestionComposerInspectorShellBridge}. */
export const QuestionComposerInspectorShellHost = QuestionComposerInspectorShellBridge
